import {useCallback, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {selectAuthDialog} from 'app/hooks/reduxCreateSelectorHooks';
import {useLocation} from 'react-router';
import {reduxSelectCurrentUser} from 'app/hooks/reduxSelectCurrentUser';
import SessionStorageManager from 'app/services/web-storage/SessionStorageManager';
import {captureMessage} from '@sentry/react';

const STORAGE_KEY = 'completeProfileRedirect';

/**
 * Handles redirect to AuthService to complete profile and verify user email
 * except if the user has used sign-up flow within auth dialog while session
 */
const useRedirectToCompleteProfile = () => {
  const sessionStorageManager = useRef(SessionStorageManager.getInstance());
  const {user, claims} = useSelector(reduxSelectCurrentUser);
  const authDialog = useSelector(selectAuthDialog);
  const location = useLocation();

  const logoutUser = useCallback(async () => {
    const {logoutCurrentUser} = await import('app/services/firebase');
    await logoutCurrentUser();
  }, []);

  useEffect(() => {
    if (authDialog) {
      return;
    }
    if (!Object.keys(user).length) {
      return;
    }
    if (claims.u && claims.email_verified) {
      return;
    }
    const sessionStorage = sessionStorageManager.current;
    if (sessionStorage.getItem(STORAGE_KEY)) {
      captureMessage('User logged out because complete-profile-flow could not finish');
      logoutUser().finally(() => sessionStorage.removeItem(STORAGE_KEY));
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, 1);
    window.location.href = `/auth/complete-profile?redirectUrl=${encodeURIComponent(location.pathname)}`;
  }, [user, claims, authDialog, location.pathname, logoutUser]);
};

export default useRedirectToCompleteProfile;
